import React, { useState } from "react";
import { Text } from "@chakra-ui/react";
import { useNavigate } from "react-router";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickLandingSection from "@/features/pathpick/features/landing/components/PathPickLandingSection";
import PathPickMembershipPlanCompareSection from "@/features/pathpick/features/membership/components/plan-compare-section/PathPickMembershipPlanCompareSection";
import { FIRST_MONTH_DISCOUNT_RATE } from "@/features/pathpick/features/membership/constants";
import { MembershipBillingCycle } from "@/features/pathpick/features/membership/types";
import { PathPickLandingCta } from "@/features/pathpick/features/landing/hooks/usePathPickLandingCta";
import { PATHPICK_LANDING_COLORS } from "@/features/pathpick/features/landing/constants";

interface Props {
  cta: PathPickLandingCta;
}

/**
 * 멤버십 플랜 소개.
 *
 * 가격·한도는 운영 멤버십 화면과 같은 `PathPickMembershipPlanCompareSection`을 그대로 쓴다 —
 * 랜딩에서 숫자를 따로 적으면 두 화면이 갈라진다.
 * 시안에 있는 영역(플랜 비교 카드 + 플랜 카드 3장)만 노출하고, 배너·현재 플랜 카드는 넣지 않는다.
 */
function PathPickLandingMembershipSection({ cta }: Props) {
  const { t } = useI18nHelper();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] =
    useState<MembershipBillingCycle>("monthly");

  // 랜딩의 플랜 카드는 결제를 접수하지 않는다 — 시작하기도 상담하기도 목적지가 같다.
  const goToPlanCta = () => navigate(cta.planCtaHref);

  return (
    <PathPickLandingSection
      title={t("path_pick_landing:membership.title")}
      description={t("path_pick_landing:membership.description")}
    >
      <PathPickMembershipPlanCompareSection
        billingCycle={billingCycle}
        onChangeBillingCycle={setBillingCycle}
        // 유저가 어떤 플랜을 쓰고 있든 랜딩의 CTA는 "플랜 시작하기 / 상담하기"로 고정한다(시안).
        // currentPlan을 주지 않으면 모든 카드가 relation "none"이 되어 그 문구가 그대로 나온다.
        currentPlan={undefined}
        isFirstSubscription={cta.isFirstSubscription}
        descriptionOverride={t(
          "path_pick_landing:membership.compare_description"
        )}
        onChangeRequest={goToPlanCta}
        onConsult={goToPlanCta}
      />
      {cta.isFirstSubscription && (
        <Text
          textStyle={"caption-medium"}
          color={PATHPICK_LANDING_COLORS.textFaint}
        >
          {t("path_pick_landing:membership.caption", {
            rate: FIRST_MONTH_DISCOUNT_RATE,
          })}
        </Text>
      )}
    </PathPickLandingSection>
  );
}

export default PathPickLandingMembershipSection;
